<template>
  <Transition name="ptr-fade">
    <div 
      v-if="isPulling || isRefreshing" 
      class="pull-to-refresh-indicator"
      :style="indicatorStyle"
    >
      <div class="ptr-content">
        <!-- Spinner при refresh -->
        <div v-if="isRefreshing" class="ptr-spinner">
          <svg class="ptr-spinner-svg" viewBox="0 0 50 50">
            <circle
              class="ptr-spinner-circle"
              cx="25"
              cy="25"
              r="20"
              fill="none"
              stroke="currentColor"
              stroke-width="4"
            />
          </svg>
        </div>

        <!-- Стрелка при pull -->
        <div v-else class="ptr-arrow" :class="{ 'ptr-arrow-ready': canRefresh }">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path 
              d="M12 5v14M19 12l-7 7-7-7" 
              stroke-width="2" 
              stroke-linecap="round" 
              stroke-linejoin="round"
            />
          </svg>
        </div>

        <!-- Текст -->
        <div class="ptr-text">
          <span v-if="isRefreshing">Обновление...</span>
          <span v-else-if="canRefresh">Отпустите для обновления</span>
          <span v-else>Потяните для обновления</span>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  /**
   * Идёт ли сейчас refresh
   */
  isRefreshing: boolean
  
  /**
   * Идёт ли сейчас pull
   */
  isPulling: boolean
  
  /**
   * Расстояние pull (в пикселях)
   */
  pullDistance: number
  
  /**
   * Достигнут ли порог для refresh
   */
  canRefresh: boolean
}

const props = defineProps<Props>()

/**
 * Динамический стиль для индикатора
 * Позиционируется в зависимости от pullDistance
 * ✅ ИСПРАВЛЕНО: Сохраняем translateX(-50%) для центрирования
 */
const indicatorStyle = computed(() => {
  if (props.isRefreshing) {
    // При refresh фиксируем на определённой позиции
    return {
      transform: 'translateX(-50%) translateY(0)',
      opacity: '1',
    }
  }

  if (props.isPulling) {
    // При pull следуем за пальцем с небольшим отступом
    const offset = Math.min(props.pullDistance - 20, 100)
    return {
      transform: `translateX(-50%) translateY(${Math.max(0, offset)}px)`,
      opacity: Math.min(props.pullDistance / 80, 1),
    }
  }

  return {
    transform: 'translateX(-50%) translateY(-60px)',
    opacity: '0',
  }
})
</script>

<style lang="scss" scoped>
.pull-to-refresh-indicator {
  position: fixed;
  top: 0;
  left: 50%;
  // ✅ transform управляется через inline стиль (включает translateX(-50%))
  z-index: 9999;
  pointer-events: none;
  transition: transform 0.2s ease-out, opacity 0.2s ease-out;
  
  // Safe area для iOS
  padding-top: env(safe-area-inset-top, 0);
}

.ptr-content {
  @apply flex flex-col items-center gap-2 px-6 py-3 rounded-full;
  @apply bg-hui-content1/95 shadow-lg backdrop-blur-md;
  @apply border border-hui-divider;
  min-width: 160px;
}

// Spinner анимация
.ptr-spinner {
  @apply w-6 h-6;
  animation: ptr-rotate 1s linear infinite;
}

.ptr-spinner-svg {
  @apply w-full h-full;
}

.ptr-spinner-circle {
  @apply text-hui-success;
  stroke-dasharray: 90, 150;
  stroke-dashoffset: 0;
  stroke-linecap: round;
  animation: ptr-dash 1.5s ease-in-out infinite;
}

@keyframes ptr-rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes ptr-dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
}

// Стрелка
.ptr-arrow {
  @apply w-6 h-6 text-hui-foreground/40;
  transition: all 0.3s ease;

  svg {
    @apply w-full h-full;
  }

  &.ptr-arrow-ready {
    @apply text-hui-success;
    transform: rotate(180deg);
  }
}

// Текст
.ptr-text {
  @apply text-sm font-medium text-hui-foreground/80;
  white-space: nowrap;
}

// Transitions
.ptr-fade-enter-active,
.ptr-fade-leave-active {
  transition: opacity 0.3s ease;
}

.ptr-fade-enter-from,
.ptr-fade-leave-to {
  opacity: 0;
}
</style>
